import { Alert } from '@mui/material';
import FormError from 'components/form-error/form-error.component';
import RegisterFields from 'components/register-fields/register-fields.component';
import StandalonePage from 'components/standalone-page/standalone-page.component';
import PasswordlessAuthentication from 'pages/checkout/passwordless-authentication/passwordless-authentication.component';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  dispatchSignInSuccess,
  passwordlessSignUpStartAsync,
} from 'redux/user/user.actions';
import { selectPasswordlessOAuthResponse } from 'redux/user/user.selectors';
import { usePasswordlessOAuthVerification } from './passwordless-activation.hooks';
import IncLoginLink from 'components/inc-login-link/inc-login-link.component';

const PasswordlessActivationPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  const code = new URLSearchParams(search).get('token');
  const isMagicLink = !!code;
  const passwordlessOAuthResponse = useSelector(
    selectPasswordlessOAuthResponse,
  );

  const { passwordlessOAuthVerify, passwordlessOAuthError } =
    usePasswordlessOAuthVerification();

  useEffect(() => {
    if (passwordlessOAuthResponse) return;

    passwordlessOAuthVerify(code, 'MAGIC_LINK');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = passwordlessOAuthResponse?.user;

  if (
    isMagicLink &&
    !passwordlessOAuthResponse?.is_new_user &&
    !passwordlessOAuthError
  ) {
    return <PasswordlessAuthentication />;
  }

  if (passwordlessOAuthError) {
    return (
      <StandalonePage title={t('passwordless.activation.title')}>
        <FormError errorMessage={passwordlessOAuthError} />
        <IncLoginLink
          label={t('passwordless.activation.loginLink.label')}
          aria-label={t('passwordless.activation.loginLink.ariaLabel')}
        />
      </StandalonePage>
    );
  }

  const prefillData = {
    email: user?.email_verified ? user.email : undefined,
    emailDisabled: !!user?.email_verified,
    phoneNumber: user?.phone_verified ? user.phone : undefined,
    phoneDisabled: !!user?.phone_verified,
  };

  const submitAction = async formData => {
    const response = await dispatch(
      passwordlessSignUpStartAsync(
        formData,
        user?.id,
        passwordlessOAuthResponse?.access_token,
      ),
    );
    if (!response?.error) {
      dispatchSignInSuccess(dispatch, {
        username: formData.email,
        data: passwordlessOAuthResponse,
        rememberMe: false,
      });

      history.push('/');
    }
    return response;
  };

  return (
    <StandalonePage title={t('passwordless.activation.title')}>
      <Alert
        severity="info"
        sx={{ mb: 2, width: '100%' }}
        data-testid="PasswordlessActivationPage.Alert.Verified"
      >
        {t('passwordless.activation.verifiedAlert')}
      </Alert>
      <RegisterFields
        registerData={{
          userData: {
            email: prefillData.email,
            phoneNumber: prefillData.phoneNumber,
          },
        }}
        submitAction={submitAction}
        showPasswordFields
      />
    </StandalonePage>
  );
};

export default PasswordlessActivationPage;
